import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_N,
  HEADER_FILL,
  LINKED_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  borderFromSpecs,
  calcEnergyTotal,
  formatNumber,
  formatUnit,
  getFyLabels,
  hasEnergyData,
  internalSheetLink,
  padMergeRow,
  parseBorderSpec,
  readAgencyNames,
  readFY,
  safeText,
  setCell,
  setGrey,
  setPlain,
  setPrinciple6ColumnWidths,
  setWhite,
  setupPrinciple6SheetView,
  sumDischargeSources,
  sumFYCells,
  toYesNo,
  toYesNoNa,
} from "./principle6SheetHelpers.js";
import { hideRowsThrough } from "./detailSheetCommon.js";

/** String constants — avoid ESM circular imports from detail sheet modules. */
const RENEWABLE_ENERGY_SHEET = "Energy consumed from renewable";
const NR_ENERGY_SHEET = "Energy consumed from NR";
const OTHER_AIR_EMISSIONS_SHEET = "Details of other air emissions";
const ENVIRONMENT_APPROVAL_SHEET = "Environment approval (clearance)";
const ENVIRONMENTAL_IMPACT_SHEET = "Environmental impact";
const WATER_STRESS_SHEET = "Plant in area of water stress";
const INITIATIVE_SHEET = "Initiative to improve resource";

const DETAIL_CELL = "D8";

const PRINCIPLE_TITLE =
  "PRINCIPLE 6: Businesses should respect and make efforts to protect and restore the environment";

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };
const THIN_ALL = "tttt";

const RENEWABLE_KEYS = ["electricityConsumption", "fuelConsumption", "energyConsumption"];
const NON_RENEWABLE_KEYS = ["electricityConsumption", "fuelConsumption", "energyConsumption"];

const AIR_ROWS = [
  ["NOx", "nox"],
  ["SOx", "sox"],
  ["Particulate matter (PM)", "pm"],
  ["Persistent organic pollutants (POP)", "pop"],
  ["Volatile organic compounds (VOC)", "voc"],
  ["Hazardous air pollutants (HAP)", "hap"],
];

const WASTE_GEN_ROWS = [
  ["Plastic waste (A)", "plastics"],
  ["E-waste (B)", "eWaste"],
  ["Bio-medical waste (C)", "bioMediacal"],
  ["Construction and demolition waste (D)", "construtionAndDemolition"],
  ["Battery waste (E)", "batteryWaste"],
  ["Radioactive waste (F)", "radioactiveWaste"],
  ["Other Hazardous waste. Please specify, if any. (G)", "otherHazardous"],
  [
    "Other Non-hazardous waste generated (H). Please specify, if any. (Break-up by composition i.e. by materials relevant to the sector)",
    "otherNonHazardous",
  ],
];

const WASTE_REC_ROWS = [
  ["(i) Recycled", "recycled"],
  ["(ii) Re-used", "reUsed"],
  ["(iii) Other recovery operations", "other"],
];

const WASTE_DISP_ROWS = [
  ["(i) Incineration", "inceneration"],
  ["(ii) Landfilling", "lanfilling"],
  ["(iii) Other disposal operations", "other"],
];

const DISCHARGE_SOURCES = [
  ["(i) To Surface water", "surfaceWater"],
  ["(ii) To Groundwater", "groundWater"],
  ["(iii) To Seawater", "producedWater"],
  ["(iv) Sent to third-parties", "thirdPartyWater"],
  ["(v) Others", "other"],
];

function dhTitleBorders() {
  return ["mmt-", "m-t-", "m-t-", "m-t-", "m-tm"];
}

function dhSectionBorders() {
  return ["tmtt", "tttt", "tttt", "tttt", "tttm"];
}

function outerDhBorders(endCol, { first = "tmt-", middle = "t-t-", last = "t-tt", edge = "---m" } = {}) {
  const specs = [];
  for (let col = COL_D; col <= COL_H; col += 1) {
    if (col > endCol && col < COL_H) specs.push("----");
    else if (col === COL_H) specs.push(edge);
    else if (col === COL_D) specs.push(first);
    else if (col === endCol) specs.push(last);
    else specs.push(middle);
  }
  return specs;
}

function applyPerCellBorders(ws, row, startCol, endCol, specs) {
  for (let col = startCol; col <= endCol; col += 1) {
    const border = borderFromSpecs(specs, col);
    if (border) ws.getCell(row, col).border = border;
  }
}

function writeMergedLabel(ws, row, startCol, endCol, text, specs, options = {}) {
  for (let col = startCol; col <= endCol; col += 1) {
    setCell(ws, row, col, col === startCol ? text : "", {
      bold: options.bold,
      font: options.font,
      align: options.align || "left",
      vertical: options.vertical || "middle",
      fill: options.fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, startCol, endCol, specs);
  if (options.height != null) ws.getRow(row).height = options.height;
}

function setHGrey(ws, row, borderSpec = "-t-m") {
  setGrey(ws, row, COL_H, parseBorderSpec(borderSpec));
}

function fmt(value) {
  return formatNumber(value);
}

function unitText(unit, fallback = "Gigajoule (GJ)") {
  const mapped = formatUnit(unit, "");
  return mapped || fallback;
}

function airUnitFor(block, key, fallback = "ug/m3") {
  const cell = block?.[key] || {};
  return unitText(cell.currentUnit || cell.previousUnit, fallback);
}

function applicabilityYesNo(flag, hasData) {
  const explicit = toYesNo(flag);
  if (explicit) return explicit;
  return hasData ? "Yes" : "";
}

function formatNonComplianceDetails(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";
  return list
    .map((item, idx) => {
      const parts = [
        safeText(item?.specifyLaw),
        safeText(item?.details),
        safeText(item?.penalties),
        safeText(item?.correctiveAction),
      ].filter((part) => part && part !== "-");
      return parts.length ? `${idx + 1}. ${parts.join("; ")}` : "";
    })
    .filter(Boolean)
    .join("\n");
}

function addPrincipleTitle(ws) {
  const specs = dhTitleBorders();
  for (let col = COL_D; col <= COL_H; col += 1) {
    setCell(ws, 6, col, col === COL_D ? PRINCIPLE_TITLE : "", {
      font: TITLE_FONT,
      align: "center",
      vertical: "middle",
      fill: HEADER_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, 6, COL_D, COL_H, specs);
  ws.getRow(6).height = 30;
}

function addSectionBand(ws, row, title, borderSpecs = null, options = {}) {
  const specs = borderSpecs || dhSectionBorders();
  const fill = options.fill !== undefined ? options.fill : NA_FILL;
  for (let col = COL_D; col <= COL_H; col += 1) {
    setCell(ws, row, col, col === COL_D ? title : "", {
      font: SECTION_FONT,
      align: "center",
      vertical: "middle",
      fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, COL_D, COL_H, specs);
  ws.getRow(row).height = 20.15;
}

/** Intro / question label merged D→endCol; optional trailing grey on H. */
function addIntro(ws, row, text, endCol = COL_G, options = {}) {
  const specs = outerDhBorders(endCol, {
    first: options.first || "tmt-",
    middle: options.middle || "t-t-",
    last: options.last || "t-tt",
    edge: options.edge || "-t-m",
  });
  writeMergedLabel(ws, row, COL_D, endCol, text, specs, {
    height: options.height,
    bold: options.bold,
  });
  for (let col = endCol + 1; col < COL_H; col += 1) {
    setGrey(ws, row, col, parseBorderSpec("----"));
  }
  if (endCol < COL_H) {
    setHGrey(ws, row, options.edge || "-t-m");
  }
}

/** Label D→labelEnd, white value at valueCol, grey after. */
function addLabelValue(ws, row, label, value, options = {}) {
  const labelEnd = options.labelEnd || COL_F;
  const valueCol = options.valueCol || labelEnd + 1;
  const specs = outerDhBorders(labelEnd, {
    first: options.first || "tmtt",
    middle: options.middle || "tttt",
    last: options.last || "ttt-",
    edge: options.edge || "-t-m",
  });
  writeMergedLabel(ws, row, COL_D, labelEnd, label, specs, {
    height: options.height ?? 30,
    bold: options.bold,
  });
  const fill = options.valueFill === undefined ? VALUE_FILL : options.valueFill;
  setCell(ws, row, valueCol, value ?? "", {
    align: options.valueAlign || "center",
    fill,
    bordered: false,
    border: parseBorderSpec(options.valueBorder || THIN_ALL),
    link: options.link,
  });
  for (let col = valueCol + 1; col < COL_H; col += 1) {
    setGrey(ws, row, col, parseBorderSpec(options.gapBorder || "ttt-"));
  }
  if (valueCol < COL_H) setHGrey(ws, row, options.edge || "-t-m");
}

function addAgencyRows(ws, ynRow, nameRow, agency, options = {}) {
  const ynLabel =
    options.ynLabel ||
    "Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)";
  const answer = toYesNo(agency?.action);
  const name = answer === "Yes" ? readAgencyNames(agency) : "";
  addLabelValue(ws, ynRow, ynLabel, answer, {
    labelEnd: options.labelEnd || COL_F,
    valueCol: options.valueCol || COL_G,
    height: options.height || 30,
    first: options.first,
    middle: options.middle,
    last: options.last,
    gapBorder: options.gapBorder,
    edge: options.edge,
    valueBorder: options.valueBorder,
  });
  addLabelValue(ws, nameRow, "If yes, name of the external agency.", name, {
    labelEnd: options.labelEnd || COL_F,
    valueCol: options.valueCol || COL_G,
    height: options.height || 30,
    valueFill: answer === "Yes" ? VALUE_FILL : NA_FILL,
    first: options.nameFirst || options.first,
    middle: options.nameMiddle || options.middle,
    last: options.nameLast || options.last,
    gapBorder: options.nameGapBorder || options.gapBorder,
    edge: options.nameEdge || options.edge,
    valueBorder: options.nameValueBorder || options.valueBorder,
  });
}

function addParamUnitFyHeader(ws, row, fyCurrent, fyPrevious, unitLabel = "Units", options = {}) {
  setCell(ws, row, COL_D, "Parameter", {
    bold: true,
    align: "left",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.dBorder || "-mtt"),
  });
  setCell(ws, row, COL_E, unitLabel, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.eBorder || "--tt"),
  });
  setCell(ws, row, COL_F, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.fBorder || "-ttt"),
  });
  setCell(ws, row, COL_G, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.gBorder || "-tt-"),
  });
  setHGrey(ws, row, options.hBorder || "-t-m");
  ws.getRow(row).height = 20.15;
}

function addParamUnitFyRow(ws, row, label, unit, curr, prev, options = {}) {
  const fill = options.fill;
  const unitFill = options.unitFill ?? fill ?? VALUE_FILL;
  const valueFill = options.valueFill ?? fill ?? VALUE_FILL;
  setCell(ws, row, COL_D, label, {
    align: "left",
    bold: options.bold,
    fill: options.labelFill,
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmt-"),
  });
  setCell(ws, row, COL_E, unit ?? "", {
    align: "center",
    fill: unitFill,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_F, fmt(curr), {
    align: "center",
    fill: valueFill,
    bordered: false,
    border: parseBorderSpec(options.fBorder || THIN_ALL),
  });
  setCell(ws, row, COL_G, fmt(prev), {
    align: "center",
    fill: valueFill,
    bordered: false,
    border: parseBorderSpec(options.gBorder || THIN_ALL),
  });
  if (options.link) {
    setCell(ws, row, COL_H, options.linkText || "Add details", {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
      border: parseBorderSpec(options.hBorder || "--tm"),
      link: options.link,
    });
  } else {
    setHGrey(ws, row, options.hBorder || "-t-m");
  }
  if (options.height != null) ws.getRow(row).height = options.height;
  else ws.getRow(row).height = 20.15;
}

function addParamFyHeader(ws, row, fyCurrent, fyPrevious, options = {}) {
  setCell(ws, row, COL_D, "Parameter", {
    bold: true,
    align: "left",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmtt"),
  });
  setCell(ws, row, COL_E, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.eBorder || "t-tt"),
  });
  setCell(ws, row, COL_F, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(options.fBorder || "ttt-"),
  });
  setGrey(ws, row, COL_G, parseBorderSpec(options.gBorder || "-t--"));
  setHGrey(ws, row, options.hBorder || "---m");
  ws.getRow(row).height = 20.15;
}

function addParamFyRow(ws, row, label, curr, prev, options = {}) {
  const fill = options.fill;
  const valueFill = options.valueFill ?? fill ?? VALUE_FILL;
  setCell(ws, row, COL_D, label, {
    align: "left",
    bold: options.bold,
    fill: options.labelFill,
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmtt"),
  });
  setCell(ws, row, COL_E, fmt(curr), {
    align: "center",
    fill: valueFill,
    bordered: false,
    border: parseBorderSpec(options.eBorder || THIN_ALL),
  });
  setCell(ws, row, COL_F, fmt(prev), {
    align: "center",
    fill: valueFill,
    bordered: false,
    border: parseBorderSpec(options.fBorder || THIN_ALL),
  });
  setGrey(ws, row, COL_G, parseBorderSpec(options.gBorder || "-t--"));
  setHGrey(ws, row, options.hBorder || "---m");
  if (options.height != null) ws.getRow(row).height = options.height;
  else ws.getRow(row).height = 20.15;
}

function addBandRow(ws, row, label, options = {}) {
  const fill = options.fill ?? undefined;
  setCell(ws, row, COL_D, label, {
    align: "left",
    fill,
    bold: options.bold,
    bordered: false,
    border: parseBorderSpec(options.dBorder || "tmt-"),
  });
  setPlain(ws, row, COL_E, "", parseBorderSpec(options.eBorder || "t---"));
  setPlain(ws, row, COL_F, "", parseBorderSpec(options.fBorder || "t-t-"));
  setPlain(ws, row, COL_G, "", parseBorderSpec(options.gBorder || "t-t-"));
  setHGrey(ws, row, options.hBorder || "-t-m");
  ws.getRow(row).height = options.height ?? 20.15;
}

function addMergedBand(ws, row, label, endCol = COL_F, options = {}) {
  const specs = outerDhBorders(endCol, {
    first: options.first || "tmt-",
    middle: options.middle || "t-t-",
    last: options.last || "t-tt",
    edge: options.edge || "---m",
  });
  writeMergedLabel(ws, row, COL_D, endCol, label, specs, {
    height: options.height ?? 20.15,
    bold: options.bold,
    fill: options.fill,
  });
  for (let col = endCol + 1; col < COL_H; col += 1) {
    setGrey(ws, row, col, parseBorderSpec(options.gapBorder || "-t--"));
  }
  if (endCol < COL_H) setHGrey(ws, row, options.edge || "---m");
}

function addDetailLinkRow(ws, row, label, sheetName, options = {}) {
  const labelEnd = options.labelEnd || COL_F;
  const valueCol = options.valueCol || COL_G;
  const linkText = options.linkText || "Add Details";
  addLabelValue(ws, row, label, linkText, {
    labelEnd,
    valueCol,
    height: options.height ?? 45,
    link: internalSheetLink(sheetName, DETAIL_CELL),
    valueFill: options.valueFill,
    first: options.first || "tmtt",
    middle: options.middle || "tttt",
    last: options.last || "ttt-",
  });
}

function numOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function intensityFrom(total, revenue) {
  const t = numOrNull(total);
  const r = numOrNull(revenue);
  if (t === null || r === null || r === 0) return "";
  return fmt(t / r);
}

export function buildPrinciple6Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 6");
  setPrinciple6ColumnWidths(ws);
  setupPrinciple6SheetView(workbook, ws);
  hideRowsThrough(ws, 3);

  const principle = sectionC?.principle6 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const energy = essential?.energyConsumptionIntensity || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);

  // —— R6–7 title / essential ——
  addPrincipleTitle(ws);
  addSectionBand(ws, 7, "Essential Indicators");

  // —— R8 Q1 intro ——
  addIntro(
    ws,
    8,
    "1. Details of total energy consumption (in Joules or multiples) and energy intensity, in the following format:",
    COL_G,
    { first: "tmt-", middle: "t-t-", last: "t-tt", height: 20.15 }
  );

  // —— R9 applicability ——
  addLabelValue(
    ws,
    9,
    "Whether total energy consumption and energy intensity is applicable to the company?",
    applicabilityYesNo(energy?.applicable ?? energy?.isApplicable, hasEnergyData(energy)),
    {
      labelEnd: COL_F,
      valueCol: COL_G,
      height: 20.15,
      first: "tm-t",
      middle: "tt-t",
      last: "tt-t",
      bold: true,
    }
  );

  // —— R10–11 revenue (merged label D10:E11) ——
  const revenueCurr =
    readFY(energy?.revenueFromOperations, "current") ||
    energy?.revenueCurrent ||
    essential?.revenueFromOperationsCurrent ||
    "";
  const revenuePrev =
    readFY(energy?.revenueFromOperations, "previous") ||
    energy?.revenuePrevious ||
    essential?.revenueFromOperationsPrevious ||
    "";

  setCell(ws, 10, COL_D, "Revenue from operations (in Rs.)", {
    bold: true,
    align: "left",
    bordered: false,
    border: parseBorderSpec("tm--"),
  });
  setCell(ws, 10, COL_E, "", {
    bold: true,
    bordered: false,
    border: parseBorderSpec("t--t"),
  });
  setCell(ws, 11, COL_D, "", {
    bold: true,
    bordered: false,
    border: parseBorderSpec("-mt-"),
  });
  setCell(ws, 11, COL_E, "", {
    bold: true,
    bordered: false,
    border: parseBorderSpec("--tt"),
  });
  ws.mergeCells(10, COL_D, 11, COL_E);
  {
    const startBorder = parseBorderSpec("tm--");
    const endBorder = parseBorderSpec("--tt");
    ws.getCell(10, COL_D).border = {
      top: startBorder.top,
      left: startBorder.left,
      bottom: endBorder.bottom,
      right: endBorder.right,
    };
  }
  setCell(ws, 10, COL_F, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setCell(ws, 10, COL_G, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttt"),
  });
  setHGrey(ws, 10);
  setWhite(ws, 11, COL_F, fmt(revenueCurr), parseBorderSpec(THIN_ALL));
  setWhite(ws, 11, COL_G, fmt(revenuePrev), parseBorderSpec(THIN_ALL));
  setHGrey(ws, 11);
  ws.getRow(10).height = 20.15;
  ws.getRow(11).height = 20.15;

  // —— R12 energy header ——
  addParamUnitFyHeader(ws, 12, fyCurrent, fyPrevious, "Units");

  const unit = unitText(
    energy?.fromRenewableSource?.electricityConsumption?.currentUnit ||
      energy?.fromRenewableSource?.electricityConsumption?.previousUnit
  );
  const intensityUnit = `${unit} / Rs.`;

  // —— R13–17 renewable ——
  addBandRow(ws, 13, "From renewable sources", { bold: true });
  const renewable = energy?.fromRenewableSource || {};
  addParamUnitFyRow(
    ws,
    14,
    "Total electricity consumption (A)",
    unit,
    readFY(renewable.electricityConsumption, "current"),
    readFY(renewable.electricityConsumption, "previous"),
    { unitFill: VALUE_FILL }
  );
  addParamUnitFyRow(
    ws,
    15,
    "Total fuel consumption (B)",
    unit,
    readFY(renewable.fuelConsumption, "current"),
    readFY(renewable.fuelConsumption, "previous"),
    { unitFill: NA_FILL, fBorder: "t--t", gBorder: "tt--", hBorder: "-ttm" }
  );
  addParamUnitFyRow(
    ws,
    16,
    "Energy consumption through other sources (C)",
    unit,
    readFY(renewable.energyConsumption, "current"),
    readFY(renewable.energyConsumption, "previous"),
    {
      unitFill: NA_FILL,
      valueFill: NA_FILL,
      link: internalSheetLink(RENEWABLE_ENERGY_SHEET, DETAIL_CELL),
      linkText: "Add details",
      hBorder: "--tm",
    }
  );
  const renTotal = calcEnergyTotal(renewable, RENEWABLE_KEYS);
  addParamUnitFyRow(
    ws,
    17,
    "Total energy consumed from renewable sources (A+B+C)",
    unit,
    renTotal.current,
    renTotal.previous,
    { unitFill: LINKED_FILL, valueFill: NA_FILL }
  );

  // —— R18–22 non-renewable ——
  addBandRow(ws, 18, "From non-renewable sources", {
    bold: true,
    eBorder: "----",
    fBorder: "--t-",
    gBorder: "--tt",
    hBorder: "---m",
  });
  const nonRenewable = energy?.fromNonRenewableSource || {};
  addParamUnitFyRow(
    ws,
    19,
    "Total electricity consumption (D)",
    unit,
    readFY(nonRenewable.electricityConsumption, "current"),
    readFY(nonRenewable.electricityConsumption, "previous"),
    { unitFill: NA_FILL }
  );
  addParamUnitFyRow(
    ws,
    20,
    "Total fuel consumption (E)",
    unit,
    readFY(nonRenewable.fuelConsumption, "current"),
    readFY(nonRenewable.fuelConsumption, "previous"),
    { unitFill: NA_FILL }
  );
  addParamUnitFyRow(
    ws,
    21,
    "Energy consumption through other sources (F)",
    unit,
    readFY(nonRenewable.energyConsumption, "current"),
    readFY(nonRenewable.energyConsumption, "previous"),
    {
      unitFill: LINKED_FILL,
      valueFill: NA_FILL,
      link: internalSheetLink(NR_ENERGY_SHEET, DETAIL_CELL),
      linkText: "Add details",
      hBorder: "t-tm",
    }
  );
  const nonTotal = calcEnergyTotal(nonRenewable, NON_RENEWABLE_KEYS);
  addParamUnitFyRow(
    ws,
    22,
    "Total energy consumed from non-renewable sources (D+E+F)",
    unit,
    nonTotal.current,
    nonTotal.previous,
    { unitFill: LINKED_FILL, valueFill: NA_FILL, height: 29, hBorder: "t--m" }
  );

  const grandCurr =
    (renTotal.current ?? 0) + (nonTotal.current ?? 0) ||
    readFY(energy?.energyIntensity?.energyIntensity, "current");
  const grandPrev =
    (renTotal.previous ?? 0) + (nonTotal.previous ?? 0) ||
    readFY(energy?.energyIntensity?.energyIntensity, "previous");

  const intensity = energy?.energyIntensity || {};
  addParamUnitFyRow(
    ws,
    23,
    "Total energy consumed (A+B+C+D+E+F)",
    unit,
    grandCurr,
    grandPrev,
    { unitFill: LINKED_FILL, valueFill: NA_FILL }
  );
  addParamUnitFyRow(
    ws,
    24,
    "Energy intensity per rupee of turnover (Total energy consumed / Revenue from operations)",
    intensityUnit,
    intensityFrom(grandCurr, revenueCurr) ||
      readFY(intensity.energyIntensityTurnover, "current"),
    intensityFrom(grandPrev, revenuePrev) ||
      readFY(intensity.energyIntensityTurnover, "previous"),
    { unitFill: LINKED_FILL, valueFill: NA_FILL, height: 29 }
  );
  addParamUnitFyRow(
    ws,
    25,
    "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)\n(Total energy consumed / Revenue from operations adjusted for PPP)",
    intensityUnit,
    readFY(intensity.energyIntensityPuchasingPower, "current"),
    readFY(intensity.energyIntensityPuchasingPower, "previous"),
    { unitFill: LINKED_FILL, valueFill: VALUE_FILL, height: 58 }
  );
  addParamUnitFyRow(
    ws,
    26,
    "Energy intensity in terms of physical Output",
    unit,
    readFY(intensity.energyPhysicalOutput, "current"),
    readFY(intensity.energyPhysicalOutput, "previous"),
    { unitFill: LINKED_FILL, valueFill: VALUE_FILL }
  );
  addParamUnitFyRow(
    ws,
    27,
    "Energy intensity (optional) – the relevant metric may be selected by the entity",
    unit,
    readFY(intensity.energyIntensityOptional, "current"),
    readFY(intensity.energyIntensityOptional, "previous"),
    { unitFill: VALUE_FILL, valueFill: VALUE_FILL, height: 29 }
  );

  addAgencyRows(ws, 28, 29, energy?.energyIndependentAssessment);

  // —— R30–31 PAT ——
  addLabelValue(
    ws,
    30,
    "2. Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India?",
    toYesNo(essential?.patSchemeDetails?.action),
    { height: 30 }
  );
  addLabelValue(
    ws,
    31,
    "If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.",
    essential?.patSchemeDetails?.action ? safeText(essential?.patSchemeDetails?.remark) : "",
    {
      height: 30,
      valueFill: essential?.patSchemeDetails?.action ? VALUE_FILL : NA_FILL,
    }
  );

  // —— R32–47 water ——
  addMergedBand(
    ws,
    32,
    "3. Provide details of the following disclosures related to water, in the following format:",
    COL_F,
    { first: "tm--", middle: "t---", last: "t---", height: 20.15, bold: true, gapBorder: "tt--" }
  );
  addParamFyHeader(ws, 33, fyCurrent, fyPrevious);
  setCell(ws, 34, COL_D, "Water withdrawal by source (in kilolitres)", {
    align: "left",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tmt-"),
  });
  setGrey(ws, 34, COL_E, parseBorderSpec("t-t-"));
  setGrey(ws, 34, COL_F, parseBorderSpec("t-t-"));
  setGrey(ws, 34, COL_G, parseBorderSpec("-t--"));
  setHGrey(ws, 34, "---m");

  const wd = essential?.waterDisclosuresRelated || {};
  const withdrawalKeys = ["surfaceWater", "groundWater", "thirdPartyWater", "seaWater", "others"];
  const waterWithdrawalRows = [
    ["(i) Surface water", "surfaceWater"],
    ["(ii) Groundwater", "groundWater"],
    ["(iii) Third party water", "thirdPartyWater"],
    ["(iv) Seawater / desalinated water", "seaWater"],
    ["(v) Others", "others"],
  ];
  waterWithdrawalRows.forEach(([label, key], idx) => {
    addParamFyRow(ws, 35 + idx, label, readFY(wd[key], "current"), readFY(wd[key], "previous"));
  });
  addParamFyRow(
    ws,
    40,
    "Total volume of water withdrawal (in kilolitres) (i + ii + iii + iv + v)",
    sumFYCells(wd, withdrawalKeys, "current") ?? readFY(wd.totalVolumnWithdrawal, "current"),
    sumFYCells(wd, withdrawalKeys, "previous") ?? readFY(wd.totalVolumnWithdrawal, "previous"),
    { valueFill: NA_FILL, height: 29 }
  );
  addParamFyRow(
    ws,
    41,
    "Total volume of water consumption (in kilolitres)",
    readFY(wd.totalWaterConsumption, "current"),
    readFY(wd.totalWaterConsumption, "previous")
  );
  addParamFyRow(
    ws,
    42,
    "Water intensity per rupee of turnover (Total water consumption / Revenue from operations)",
    intensityFrom(readFY(wd.totalWaterConsumption, "current"), revenueCurr) ||
      readFY(wd.waterIntensityTurnover, "current"),
    intensityFrom(readFY(wd.totalWaterConsumption, "previous"), revenuePrev) ||
      readFY(wd.waterIntensityTurnover, "previous"),
    { valueFill: LINKED_FILL, height: 35.15 }
  );
  addParamFyRow(
    ws,
    43,
    "Water intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)\n(Total water consumption / Revenue from operations adjusted for PPP)",
    readFY(wd.waterIntensityPuchasingPower, "current"),
    readFY(wd.waterIntensityPuchasingPower, "previous"),
    { height: 65.15 }
  );
  addParamFyRow(
    ws,
    44,
    "Water intensity in terms of physical output",
    readFY(wd.waterIntensityPhyiscalOutput, "current"),
    readFY(wd.waterIntensityPhyiscalOutput, "previous")
  );
  addParamFyRow(
    ws,
    45,
    "Water intensity (optional) – the relevant metric may be selected by the entity",
    readFY(wd.waterIntensityOptional, "current"),
    readFY(wd.waterIntensityOptional, "previous"),
    { height: 35.15, gBorder: "-tt-" }
  );
  addAgencyRows(ws, 46, 47, wd?.externalAgency, {
    ynLabel:
      "Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency?",
    height: 31.5,
  });
  ws.getRow(47).height = 30;

  // —— R48–68 water discharge ——
  addMergedBand(
    ws,
    48,
    "4. Provide the following details related to water discharged:",
    COL_F,
    { height: 20.15, bold: true }
  );
  addParamFyHeader(ws, 49, fyCurrent, fyPrevious, {
    dBorder: "-mtt",
    eBorder: "-ttt",
    fBorder: "-tt-",
    gBorder: "-t--",
  });
  addMergedBand(
    ws,
    50,
    "Water discharge by destination and level of treatment (in kilolitres)",
    COL_F,
    { height: 20.15, bold: true }
  );

  let dischargeRow = 51;
  DISCHARGE_SOURCES.forEach(([label, key]) => {
    const c = essential?.waterDischargeCurrent?.[key] || {};
    const p = essential?.waterDischargePrevious?.[key] || {};
    const destCurr =
      (numOrNull(c.dischargeWithoutTreatment) ?? 0) + (numOrNull(c.dischargeAfterTreatment) ?? 0);
    const destPrev =
      (numOrNull(p.dischargeWithoutTreatment) ?? 0) + (numOrNull(p.dischargeAfterTreatment) ?? 0);
    const hasDest =
      numOrNull(c.dischargeWithoutTreatment) !== null ||
      numOrNull(c.dischargeAfterTreatment) !== null ||
      numOrNull(p.dischargeWithoutTreatment) !== null ||
      numOrNull(p.dischargeAfterTreatment) !== null;

    addParamFyRow(
      ws,
      dischargeRow,
      label,
      hasDest ? destCurr : "",
      hasDest ? destPrev : "",
      { valueFill: NA_FILL }
    );
    dischargeRow += 1;
    addParamFyRow(
      ws,
      dischargeRow,
      "No treatment",
      c.dischargeWithoutTreatment,
      p.dischargeWithoutTreatment
    );
    dischargeRow += 1;
    addParamFyRow(
      ws,
      dischargeRow,
      "With treatment – please specify level of treatment",
      c.dischargeAfterTreatment,
      p.dischargeAfterTreatment
    );
    dischargeRow += 1;
  });

  const dischargeKeys = ["surfaceWater", "groundWater", "producedWater", "thirdPartyWater", "other"];
  addParamFyRow(
    ws,
    66,
    "Total water discharged (in kilolitres)",
    sumDischargeSources(essential?.waterDischargeCurrent, dischargeKeys) ??
      essential?.waterDischargeCurrent?.total,
    sumDischargeSources(essential?.waterDischargePrevious, dischargeKeys) ??
      essential?.waterDischargePrevious?.total,
    { valueFill: NA_FILL, gBorder: "-tt-" }
  );
  addAgencyRows(ws, 67, 68, essential?.waterDischargeExternalAgency);

  // —— R69–70 ZLD ——
  addLabelValue(
    ws,
    69,
    "5. Has the entity implemented a mechanism for Zero Liquid Discharge?",
    toYesNo(essential?.zeroLiquidDischarge?.action),
    { labelEnd: COL_E, valueCol: COL_F, height: 30 }
  );
  setGrey(ws, 69, COL_G, parseBorderSpec("t-t-"));
  addLabelValue(
    ws,
    70,
    "If yes, provide details of its coverage and implementation.",
    essential?.zeroLiquidDischarge?.action
      ? safeText(essential?.zeroLiquidDischarge?.remark)
      : "",
    {
      labelEnd: COL_E,
      valueCol: COL_F,
      height: 30,
      valueFill: essential?.zeroLiquidDischarge?.action ? VALUE_FILL : NA_FILL,
      gapBorder: "ttt-",
    }
  );
  setGrey(ws, 70, COL_G, parseBorderSpec("ttt-"));

  // —— R71–82 air emissions ——
  addIntro(
    ws,
    71,
    "6. Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:",
    COL_G,
    { height: 20.15 }
  );
  const air = essential?.airEmissons || {};
  const airHasData = AIR_ROWS.some(
    ([, key]) =>
      String(readFY(air[key], "current")).trim() !== "" ||
      String(readFY(air[key], "previous")).trim() !== ""
  );
  addLabelValue(
    ws,
    72,
    "Whether air emissions (other than GHG emissions) by the entity is applicable to the company?",
    applicabilityYesNo(air?.applicable ?? essential?.airEmissionsApplicable, airHasData),
    { height: 20.15, bold: true }
  );
  addParamUnitFyHeader(ws, 73, fyCurrent, fyPrevious, "Please specify unit", {
    dBorder: "tmtt",
    eBorder: "tt-t",
    fBorder: "tttt",
    gBorder: "tttt",
    hBorder: "---m",
  });
  AIR_ROWS.forEach(([label, key], idx) => {
    const fallback = key === "voc" ? "Parts Per Million (PPM)" : "ug/m3";
    addParamUnitFyRow(
      ws,
      74 + idx,
      label,
      airUnitFor(air, key, fallback),
      readFY(air[key], "current"),
      readFY(air[key], "previous"),
      { unitFill: VALUE_FILL }
    );
  });
  setCell(ws, 80, COL_D, "Others – please specify", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  // Keep E–G unmerged so per-cell borders match the reference.
  setCell(ws, 80, COL_E, "Add details", {
    align: "center",
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("-tt-"),
    link: internalSheetLink(OTHER_AIR_EMISSIONS_SHEET, DETAIL_CELL),
  });
  setCell(ws, 80, COL_F, "", {
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("t-t-"),
  });
  setCell(ws, 80, COL_G, "", {
    fill: VALUE_FILL,
    bordered: false,
    border: parseBorderSpec("t-tt"),
  });
  setHGrey(ws, 80, "-t-m");
  addAgencyRows(ws, 81, 82, essential?.airEmissionAssessment);

  // —— R83–93 GHG ——
  addIntro(
    ws,
    83,
    "7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity, in the following format:",
    COL_G,
    { height: 30 }
  );
  const ghg = essential?.greenHouseGasEmissions || {};
  const ghgHasData =
    String(readFY(ghg.scope1, "current")).trim() !== "" ||
    String(readFY(ghg.scope2, "current")).trim() !== "";
  addLabelValue(
    ws,
    84,
    "Whether greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity is applicable to the company?",
    applicabilityYesNo(ghg?.applicable, ghgHasData),
    { height: 20.15, bold: true }
  );
  addParamUnitFyHeader(ws, 85, fyCurrent, fyPrevious, "Unit", {
    dBorder: "tmtt",
    eBorder: "tt-t",
    fBorder: "tttt",
    gBorder: "tttt",
    hBorder: "---m",
  });
  const ghgUnit = unitText(ghg?.scope1?.currentUnit || ghg?.scope1?.previousUnit, "MtCO2e");
  addParamUnitFyRow(
    ws,
    86,
    "Total Scope 1 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    ghgUnit,
    readFY(ghg.scope1, "current"),
    readFY(ghg.scope1, "previous"),
    { unitFill: VALUE_FILL, height: 29 }
  );
  addParamUnitFyRow(
    ws,
    87,
    "Total Scope 2 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    ghgUnit,
    readFY(ghg.scope2, "current"),
    readFY(ghg.scope2, "previous"),
    { unitFill: NA_FILL, height: 29 }
  );
  const scope12Curr =
    (numOrNull(readFY(ghg.scope1, "current")) ?? 0) + (numOrNull(readFY(ghg.scope2, "current")) ?? 0);
  const scope12Prev =
    (numOrNull(readFY(ghg.scope1, "previous")) ?? 0) + (numOrNull(readFY(ghg.scope2, "previous")) ?? 0);
  addParamUnitFyRow(
    ws,
    88,
    "Total Scope 1 and Scope 2 emission intensity per rupee of turnover\n(Total Scope 1 and Scope 2 GHG emissions / Revenue from operations)",
    "MtCO2e / Rs.",
    intensityFrom(scope12Curr, revenueCurr) ||
      readFY(ghg.totalOfScope1and2Intensity, "current"),
    intensityFrom(scope12Prev, revenuePrev) ||
      readFY(ghg.totalOfScope1and2Intensity, "previous"),
    { unitFill: NA_FILL, valueFill: NA_FILL, height: 65.15 }
  );
  addParamUnitFyRow(
    ws,
    89,
    "Total Scope 1 and Scope 2 emission intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)\n(Total Scope 1 and Scope 2 GHG emissions / Revenue from operations adjusted for PPP)",
    "MtCO2e / Rs.",
    readFY(ghg.scope1and2emissionPPP, "current"),
    readFY(ghg.scope1and2emissionPPP, "previous"),
    { unitFill: NA_FILL, valueFill: VALUE_FILL, height: 75 }
  );
  addParamUnitFyRow(
    ws,
    90,
    "Total Scope 1 and Scope 2 emission intensity in terms of physical output",
    "MtCO2e",
    readFY(ghg.scope1and2emissionIntensityPhysical, "current"),
    readFY(ghg.scope1and2emissionIntensityPhysical, "previous"),
    { unitFill: NA_FILL, valueFill: VALUE_FILL, height: 30 }
  );
  addParamUnitFyRow(
    ws,
    91,
    "Total Scope 1 and Scope 2 emission intensity (optional) – the relevant metric may be selected by the entity",
    "MtCO2e",
    readFY(ghg.scope1and2EmissionIntensity, "current"),
    readFY(ghg.scope1and2EmissionIntensity, "previous"),
    { unitFill: VALUE_FILL, valueFill: VALUE_FILL, height: 45 }
  );
  addAgencyRows(ws, 92, 93, ghg?.externalAgency, {
    labelEnd: COL_E,
    valueCol: COL_F,
    first: "-mt-",
    middle: "--t-",
    last: "-ttt",
    gapBorder: "-t--",
    edge: "---m",
    nameFirst: "tmt-",
    nameMiddle: "t-tt",
    nameLast: "tttt",
    nameGapBorder: "----",
    nameEdge: "---m",
    nameValueBorder: "tttt",
  });

  // —— R94–95 GHG projects ——
  addLabelValue(
    ws,
    94,
    "8. Does the entity have any project related to reducing Green House Gas emission? ",
    toYesNo(essential?.greenHouseGasBooleanEmissions?.action),
    { labelEnd: COL_E, valueCol: COL_F, height: 30 }
  );
  setGrey(ws, 94, COL_G, parseBorderSpec("ttt-"));
  addLabelValue(
    ws,
    95,
    "If Yes, then provide details.",
    essential?.greenHouseGasBooleanEmissions?.action
      ? safeText(essential?.greenHouseGasBooleanEmissions?.remark)
      : "",
    {
      labelEnd: COL_E,
      valueCol: COL_F,
      height: 30,
      valueFill: essential?.greenHouseGasBooleanEmissions?.action ? VALUE_FILL : NA_FILL,
      gapBorder: "tt--",
      edge: "---m",
    }
  );
  setGrey(ws, 95, COL_G, parseBorderSpec("tt--"));

  // —— R96–125 waste ——
  addMergedBand(
    ws,
    96,
    "9. Provide details related to waste management by the entity, in the following format:",
    COL_F,
    { height: 30 }
  );
  addParamFyHeader(ws, 97, fyCurrent, fyPrevious, {
    eBorder: "tttt",
    fBorder: "tttt",
    gBorder: "----",
  });
  setGrey(ws, 97, COL_G, parseBorderSpec("----"));
  addMergedBand(ws, 98, "Total Waste generated (in metric tonnes)", COL_F, {
    height: 20.15,
  });

  const wasteMgmt = essential?.wasteManagementDetails || essential;
  const wg = wasteMgmt?.wasteGenerated || essential?.wasteGenerated || {};
  const wi = wasteMgmt?.wasteIntensity || essential?.wasteIntensity || {};
  const wr = wasteMgmt?.wasteGeneratedCategory || essential?.wasteGeneratedCategory || {};
  const wdisp = wasteMgmt?.wasteDesposed || essential?.wasteDesposed || {};

  WASTE_GEN_ROWS.forEach(([label, key], idx) => {
    addParamFyRow(
      ws,
      99 + idx,
      label,
      readFY(wg[key], "current"),
      readFY(wg[key], "previous"),
      { height: idx === 7 ? 43.5 : 20.15 }
    );
  });
  const genKeys = WASTE_GEN_ROWS.map(([, key]) => key);
  addParamFyRow(
    ws,
    107,
    "Total (A+B + C + D + E + F + G + H)",
    sumFYCells(wg, genKeys, "current"),
    sumFYCells(wg, genKeys, "previous"),
    { valueFill: NA_FILL }
  );
  addParamFyRow(
    ws,
    108,
    "Waste intensity per rupee of turnover\n(Total waste generated / Revenue from operations)",
    intensityFrom(sumFYCells(wg, genKeys, "current"), revenueCurr) ||
      readFY(wi.wasteIntensity, "current"),
    intensityFrom(sumFYCells(wg, genKeys, "previous"), revenuePrev) ||
      readFY(wi.wasteIntensity, "previous"),
    { valueFill: LINKED_FILL, height: 30 }
  );
  addParamFyRow(
    ws,
    109,
    "Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)\n(Total waste generated / Revenue from operations adjusted for PPP)",
    readFY(wi.wasteIntensityPPP, "current"),
    readFY(wi.wasteIntensityPPP, "previous"),
    { height: 65.15 }
  );
  addParamFyRow(
    ws,
    110,
    "Waste intensity in terms of physical output",
    readFY(wi.wasteIntensityPhysical, "current"),
    readFY(wi.wasteIntensityPhysical, "previous")
  );
  addParamFyRow(
    ws,
    111,
    "Waste intensity (optional) – the relevant metric may be selected by the entity",
    readFY(wi.wasteIntensityOptiomal, "current"),
    readFY(wi.wasteIntensityOptiomal, "previous"),
    { height: 29, gBorder: "----" }
  );

  addMergedBand(
    ws,
    112,
    "For each category of waste generated, total waste recovered through recycling, re-using or other recovery operations (in metric tonnes)",
    COL_F,
    { height: 30, bold: true, gapBorder: "-t--" }
  );
  addMergedBand(ws, 113, "Category of waste", COL_F, { height: 20.15 });
  WASTE_REC_ROWS.forEach(([label, key], idx) => {
    addParamFyRow(ws, 114 + idx, label, readFY(wr[key], "current"), readFY(wr[key], "previous"), {
      dBorder: idx === 0 ? "-mtt" : "tmtt",
    });
  });
  const recKeys = WASTE_REC_ROWS.map(([, key]) => key);
  addParamFyRow(
    ws,
    117,
    "Total",
    sumFYCells(wr, recKeys, "current"),
    sumFYCells(wr, recKeys, "previous"),
    { valueFill: NA_FILL, dBorder: "tm-t", gBorder: "-t--" }
  );

  addMergedBand(
    ws,
    118,
    "For each category of waste generated, total waste disposed by nature of disposal method (in metric tonnes)",
    COL_F,
    { height: 20.15, bold: true, gapBorder: "-t--" }
  );
  addMergedBand(ws, 119, "Category of waste", COL_F, { height: 20.15 });
  WASTE_DISP_ROWS.forEach(([label, key], idx) => {
    addParamFyRow(
      ws,
      120 + idx,
      label,
      readFY(wdisp[key], "current"),
      readFY(wdisp[key], "previous"),
      { dBorder: idx === 0 ? "-mtt" : "tmtt" }
    );
  });
  const dispKeys = WASTE_DISP_ROWS.map(([, key]) => key);
  addParamFyRow(
    ws,
    123,
    "Total",
    sumFYCells(wdisp, dispKeys, "current"),
    sumFYCells(wdisp, dispKeys, "previous"),
    { valueFill: NA_FILL }
  );

  const wasteAssessment = wasteMgmt?.independentAssessment ?? essential?.independentAssessment;
  addAgencyRows(ws, 124, 125, wasteAssessment, { height: 31.5 });
  ws.getRow(125).height = 30;

  // —— R126–130 Q10–13 ——
  addLabelValue(
    ws,
    126,
    "10. Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.",
    safeText(essential?.wasterManagmentPractices),
    { height: 46.5 }
  );
  addDetailLinkRow(
    ws,
    127,
    "11. If the entity has operations/offices in/around ecologically sensitive areas (such as national parks, wildlife sanctuaries, biosphere reserves, wetlands, biodiversity hotspots, forests, coastal regulation zones etc.) where environmental approvals / clearances are required, please specify details in the following format:",
    ENVIRONMENT_APPROVAL_SHEET,
    { height: 45 }
  );
  addDetailLinkRow(
    ws,
    128,
    "12. Details of environmental impact assessments of projects undertaken by the entity based on applicable laws, in the current financial year:",
    ENVIRONMENTAL_IMPACT_SHEET,
    { height: 30.75 }
  );
  addLabelValue(
    ws,
    129,
    "13. Is the entity compliant with the applicable environmental law/ regulations/ guidelines in India; such as the Water (Prevention and Control of Pollution) Act, Air (Prevention and Control of Pollution) Act, Environment protection act and rules thereunder (Y/N/NA).",
    toYesNoNa(essential?.entityComplain),
    { labelEnd: COL_E, valueCol: COL_F, height: 67.5 }
  );
  setGrey(ws, 129, COL_G, parseBorderSpec("t-t-"));
  const nonCompliance =
    String(essential?.entityComplain) === "false"
      ? formatNonComplianceDetails(essential?.entityComplainwithEnvironmentLaw)
      : "";
  addLabelValue(
    ws,
    130,
    "If not, provide details of all such non-compliances, in the following format:",
    nonCompliance,
    {
      labelEnd: COL_E,
      valueCol: COL_F,
      height: 30,
      valueFill: nonCompliance ? VALUE_FILL : NA_FILL,
      first: "tm--",
      middle: "t--t",
      last: "tt-t",
      gapBorder: "t---",
    }
  );
  setGrey(ws, 130, COL_G, parseBorderSpec("t---"));

  // —— R131 Leadership ——
  addSectionBand(ws, 131, "Leadership Indicators\n", [
    "tmt-",
    "t-t-",
    "t-t-",
    "t-t-",
    "t-tm",
  ]);
  ws.getRow(131).height = 21.75;

  addIntro(
    ws,
    132,
    "1. Water withdrawal, consumption and discharge in areas of water stress (in kilolitres):",
    COL_G,
    { first: "-mtt", middle: "-ttt", last: "-tt-", height: 20.15 }
  );
  addDetailLinkRow(
    ws,
    133,
    "For each facility / plant located in areas of water stress, provide the following information:",
    WATER_STRESS_SHEET,
    { height: 30 }
  );
  addAgencyRows(ws, 134, 135, leadership?.waterIndependantAssessment);

  addIntro(
    ws,
    136,
    "2. Please provide details of total Scope 3 emissions & its intensity, in the following format:",
    COL_G,
    { first: "tm--", middle: "t---", last: "t---", height: 30 }
  );
  const scope3 = leadership?.scope3EmissionIntensity || {};
  const scope3Has =
    String(readFY(scope3.totalScope3Emission, "current")).trim() !== "" ||
    String(readFY(scope3.totalScope3Emission, "previous")).trim() !== "";
  addLabelValue(
    ws,
    137,
    "Whether total Scope 3 emissions & its intensity is applicable to the company?",
    applicabilityYesNo(scope3?.applicable, scope3Has),
    { height: 20.15, bold: true }
  );
  addParamUnitFyHeader(ws, 138, fyCurrent, fyPrevious, "Unit", {
    dBorder: "tmt-",
    eBorder: "tttt",
    fBorder: "t-tt",
    gBorder: "tttt",
    hBorder: "---m",
  });
  const scope3Unit = unitText(
    scope3?.totalScope3Emission?.currentUnit || scope3?.totalScope3Emission?.previousUnit,
    "MtCO2e"
  );
  addParamUnitFyRow(
    ws,
    139,
    "Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
    scope3Unit,
    readFY(scope3.totalScope3Emission, "current"),
    readFY(scope3.totalScope3Emission, "previous"),
    { unitFill: VALUE_FILL, height: 30, dBorder: "-mt-" }
  );
  addParamUnitFyRow(
    ws,
    140,
    "Total Scope 3 emissions per rupee of turnover",
    "MtCO2e / Rs.",
    readFY(scope3.totalScope3EmissionPerRupeeturnOver, "current"),
    readFY(scope3.totalScope3EmissionPerRupeeturnOver, "previous"),
    { unitFill: NA_FILL, valueFill: VALUE_FILL, bold: true }
  );
  addParamUnitFyRow(
    ws,
    141,
    "Total Scope 3 emission intensity (optional) – the relevant metric may be selected by the entity",
    "MtCO2e",
    readFY(scope3.emissionIntensity, "current"),
    readFY(scope3.emissionIntensity, "previous"),
    { unitFill: VALUE_FILL, valueFill: VALUE_FILL, height: 29, dBorder: "tm--" }
  );
  addAgencyRows(ws, 142, 143, leadership?.externalAgency ?? scope3?.externalAgency);

  addLabelValue(
    ws,
    144,
    "3. With respect to the ecologically sensitive areas reported at Question 10 of Essential Indicators above, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.",
    safeText(leadership?.bioDiversityPrevention),
    { height: 45, valueFill: undefined }
  );
  // value cell without white fill when matching ref (none fill on G) — keep white for inputs
  addDetailLinkRow(
    ws,
    145,
    "4. If the entity has undertaken any specific initiatives or used innovative technology or solutions to improve resource efficiency, or reduce impact due to emissions / effluent discharge / waste generated, please provide details of the same as well as outcome of such initiatives, as per the following format:",
    INITIATIVE_SHEET,
    { height: 45.75 }
  );

  addLabelValue(
    ws,
    146,
    "5. Does the entity have a business continuity and disaster management plan?",
    toYesNo(leadership?.busineessDisasterPlan?.action),
    { labelEnd: COL_E, valueCol: COL_F, height: 30 }
  );
  setGrey(ws, 146, COL_G, parseBorderSpec("t-t-"));
  addLabelValue(
    ws,
    147,
    "Details of entity at which business continuity and disaster management plan is placed or weblink.",
    leadership?.busineessDisasterPlan?.action
      ? [leadership?.busineessDisasterPlan?.remark, leadership?.busineessDisasterPlan?.webLink]
          .filter(Boolean)
          .join(" | ")
      : "",
    {
      labelEnd: COL_E,
      valueCol: COL_F,
      height: 30,
      valueFill: leadership?.busineessDisasterPlan?.action ? VALUE_FILL : NA_FILL,
      gapBorder: "ttt-",
    }
  );
  setGrey(ws, 147, COL_G, parseBorderSpec("ttt-"));

  addLabelValue(
    ws,
    148,
    "6. Disclose any significant adverse impact to the environment, arising from the value chain of\nthe entity. What mitigation or adaptation measures have been taken by the entity in this regard.",
    safeText(leadership?.disCloseAdverseImpactEnvironment),
    { height: 30 }
  );
  addLabelValue(
    ws,
    149,
    "7. Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.",
    safeText(leadership?.valueChainPartnerAssessment),
    { height: 30 }
  );

  addIntro(
    ws,
    150,
    "8. How many Green Credits have been generated or procured:",
    COL_G,
    { height: 20.15, bold: true }
  );
  const greenA = [leadership?.greenCreditsByEntityCount, leadership?.greenCreditsByEntityRemark]
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .join(" - ");
  const greenB = [
    leadership?.greenCreditsByValueChainPartnerCount,
    leadership?.greenCreditsByValueChainPartnerRemark,
  ]
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .join(" - ");
  addLabelValue(ws, 151, "a. By the listed entity", greenA, {
    labelEnd: COL_F,
    valueCol: COL_G,
    height: 20.15,
  });
  addLabelValue(
    ws,
    152,
    "b. By the top ten (in terms of value of purchases and sales, respectively) value chain partners",
    greenB,
    { labelEnd: COL_F, valueCol: COL_G, height: 20.15 }
  );

  addLabelValue(
    ws,
    153,
    "Notes",
    safeText(principle?.notes || leadership?.notes || essential?.notes),
    {
      labelEnd: COL_F,
      valueCol: COL_G,
      height: 30,
      first: "tmm-",
      middle: "t-m-",
      last: "t-mt",
      valueBorder: "ttm-",
      edge: "-tmm",
    }
  );

  // Ensure H grey on a few rows that use COL_F as value
  [69, 70, 94, 95, 129, 130, 146, 147].forEach((r) => {
    if (!ws.getCell(r, COL_H).fill) setHGrey(ws, r);
  });

  return ws;
}
